import { useState, useEffect } from 'react';
import { InMemoryCache } from '@apollo/client';
import type { ApolloClient } from '@apollo/client';
import { ApolloProvider } from '@apollo/client/react';
import { Toaster } from 'sonner';
import { createApolloClient, restoreCache } from '@scio3/graphql-client';
import { AuthProvider } from '@/contexts/AuthProvider';
import { BrandingProvider } from '@/features/branding/BrandingProvider';
import { AppRouter } from '@/routes';
import { getTokenString } from '@/lib/firebase';
import { useCachePersistence } from '@/hooks/useCachePersistence';
import { ConfirmDialog } from '@/components/shared';
import { prefetchRouteChunks } from '@/lib/prefetch-routes';

export function App() {
  const [apolloClient, setApolloClient] = useState<ApolloClient | null>(null);

  // Prefetch Templates + Designer chunks on app mount (auth-independent).
  // JS chunk downloads don't need authentication — start early for faster navigation.
  useEffect(() => {
    prefetchRouteChunks();
  }, []);

  useEffect(() => {
    async function init() {
      const cache = new InMemoryCache({
        typePolicies: {
          // ScheduleRepeatObject uses `id` as a repeat-type key ("daily", "weekly", etc.)
          // NOT a unique entity ID. Without keyFields:false, all items sharing the same
          // repeat type would reference a single shared cache entry, causing cross-query
          // overwrites (e.g., refetchQueries strips DTSTART from the rrule string).
          ScheduleRepeatObject: { keyFields: false },
          // PlaylistItem _id is the referenced asset ID, NOT unique per item.
          // Duplicate assets in a playlist share the same _id but different uuid.
          // Without this, Apollo merges duplicates into one cache entry, causing
          // duration edits on one item to overwrite the other.
          PlaylistItem: { keyFields: false },
          // Company sub-objects lack _id fields. Without merge policies Apollo
          // cannot reconcile incoming data with cached data from other queries
          // (e.g. GetCompanyInfo vs GetCompanyPreferences), causing stale reads.
          Company: {
            fields: {
              feature: { merge: true },
              featureData: { merge: true },
              limitation: { merge: true },
            },
          },
        },
      });
      await restoreCache(cache);
      const client = createApolloClient({
        getToken: getTokenString,
        graphqlUrl: import.meta.env.VITE_GRAPHQL_URL,
        cache,
      });
      setApolloClient(client);
    }
    init();
  }, []);

  if (!apolloClient) return null;

  return (
    <ApolloProvider client={apolloClient}>
      <CachePersistenceManager />
      <BrandingProvider>
        <AuthProvider>
          <AppRouter />
          {/* z-index must sit above every app overlay so confirmation toasts are never
            hidden behind the header/banner or a full-screen overlay (#1369). The app's
            highest overlays (Scio1Frame, WelcomeFlow) use z-[9999]; tying at 9999 let the
            toast lose by DOM order. Use sonner's max default so toasts always render on top. */}
          <Toaster position="top-right" richColors closeButton style={{ zIndex: 999999999 }} />
          <ConfirmDialog />
        </AuthProvider>
      </BrandingProvider>
    </ApolloProvider>
  );
}

/** Activates auto-persist to IndexedDB. Must be inside ApolloProvider. */
function CachePersistenceManager() {
  useCachePersistence();
  return null;
}
